/**
 * Compositional parts shared by the long marketing pages (/about, /api,
 * /pricing).
 *
 * `LandingKit` holds the primitives the landing page itself needs. These are
 * the patterns that only start repeating once a page runs to a dozen sections:
 * the recessed band, the standard card, the hairline-ruled row. Each was
 * inlined three or four times before it moved here.
 */
import React from 'react';
import { View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Reveal from '@/components/Landing/Reveal';
import {
  DisplayHeading,
  Kicker,
  Lede,
  useLanding,
} from '@/components/Landing/LandingKit';
import { MONO, labelType } from '@/components/Landing/landingTokens';

/**
 * Full-bleed recessed panel that separates one movement from the next. A flat
 * tone and a hairline edge are all it needs; the content provides the contrast.
 * (`glow` is accepted for call-site compatibility and intentionally ignored.)
 */
// eslint-disable-next-line no-unused-vars
export function Band({ children, glow, edges = 'both' }) {
  const { band } = useLanding();
  const showTop = edges === 'both' || edges === 'top';
  const showBottom = edges === 'both' || edges === 'bottom';

  return (
    <View
      style={{
        width: '100%',
        alignSelf: 'stretch',
        alignItems: 'center',
        backgroundColor: band.bg,
        overflow: 'hidden',
        borderTopWidth: showTop ? 1 : 0,
        borderBottomWidth: showBottom ? 1 : 0,
        borderColor: band.rule,
      }}
    >
      {children}
    </View>
  );
}

/** The standard card: hairline border on the raised surface. */
export function Card({ children, style, tone, padded = true }) {
  const { c, band, isXS } = useLanding();
  const onBand = tone === 'band';

  return (
    <View
      style={[
        {
          borderRadius: 20,
          borderWidth: 1,
          borderColor: onBand ? band.rule : c.rule,
          backgroundColor: onBand ? band.surfaceSoft : c.cardSolid,
          padding: padded ? (isXS ? 20 : 26) : 0,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/** Rounded accent tile behind a glyph. The pages' only repeated ornament. */
export function IconTile({ name, size = 38, tone = 'accent' }) {
  const { c, band } = useLanding();
  const isAccent = tone === 'accent';
  const isBand = tone === 'band';

  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.32),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isAccent ? c.accentSoft : isBand ? band.surface : c.panel,
      }}
      aria-hidden
    >
      <Icon
        name={name}
        size={Math.round(size * 0.42)}
        color={isAccent ? c.accent : isBand ? band.muted : c.muted}
        solid
      />
    </View>
  );
}

/**
 * Accent check plus a short claim, used for promise and inclusion lists.
 * `included={false}` renders the absence instead, muted and without the accent.
 */
export function CheckLine({ children, icon = 'check', tone, included = true, size = 13.5 }) {
  const { c, band } = useLanding();
  const onBand = tone === 'band';
  const glyph = included ? icon : 'xmark';
  const glyphColor = included ? c.accent : (onBand ? band.faint : c.faint);
  const textColor = included ? (onBand ? band.muted : c.muted) : (onBand ? band.faint : c.faint);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <View style={{ width: 11, alignItems: 'center', paddingTop: Math.round(size * 0.32) }}>
        <Icon name={glyph} size={11} color={glyphColor} solid />
      </View>
      <Text
        weight="medium"
        style={{
          color: textColor,
          fontSize: size,
          lineHeight: Math.round(size * 1.5),
          flex: 1,
          minWidth: 0,
          ...(included ? {} : { textDecorationLine: 'none' }),
        }}
      >
        {children}
      </Text>
    </View>
  );
}

/** Monospaced ordinal for numbered sequences. Tabular so the column stays true. */
export function Ordinal({ index }) {
  const { c } = useLanding();
  return (
    <Text
      weight="bold"
      style={{
        color: c.accent,
        fontFamily: MONO,
        fontSize: 11,
        lineHeight: 20,
        fontVariant: ['tabular-nums'],
      }}
    >
      {String(index + 1).padStart(2, '0')}
    </Text>
  );
}

/** Small all-caps label used to title a sub-block inside a section. */
export function MicroLabel({ children, tone, style }) {
  const { c, band } = useLanding();
  return (
    <Text
      weight="bold"
      style={[{ color: tone === 'band' ? band.muted : c.faint, ...labelType(9.5, 0.18) }, style]}
    >
      {children}
    </Text>
  );
}

/** Monospaced inline code, for endpoint paths and env var names. */
export function Mono({ children, tone, size = 11.5 }) {
  const { c, band } = useLanding();
  return (
    <Text
      style={{
        fontFamily: MONO,
        fontSize: size,
        lineHeight: Math.round(size * 1.5),
        color: tone === 'band' ? band.muted : c.muted,
      }}
    >
      {children}
    </Text>
  );
}

/**
 * Columns separated by hairlines rather than card borders, collapsing to a
 * stack with horizontal rules between them.
 *
 * This is the pages' quietest way to group peers — a figures strip, a set of
 * pillars, a medium breakdown. `edges` controls the outer rules: 'both' closes
 * the block top and bottom, 'top' leaves the last row open to the page.
 */
export function RuledGrid({ items, renderItem, keyOf, tone, edges = 'top', style }) {
  const { c, band, isCompact, width } = useLanding();
  const rule = tone === 'band' ? band.rule : c.rule;
  const pad = width > 900 ? 34 : 24;
  const closesTop = edges === 'both' || edges === 'top';
  const closesBottom = edges === 'both' || edges === 'bottom';

  return (
    <View
      style={[
        {
          flexDirection: isCompact ? 'column' : 'row',
          borderTopWidth: closesTop ? 1 : 0,
          borderBottomWidth: closesBottom ? 1 : 0,
          borderColor: rule,
        },
        style,
      ]}
    >
      {items.map((item, index) => (
        <View
          key={keyOf ? keyOf(item, index) : (item.title || item.label || index)}
          style={{
            flex: isCompact ? undefined : 1,
            minWidth: 0,
            paddingTop: closesTop || index > 0 ? pad : 0,
            paddingBottom: isCompact && index < items.length - 1 ? pad : 0,
            paddingRight: isCompact ? 0 : 26,
            paddingLeft: !isCompact && index > 0 ? 26 : 0,
            borderLeftWidth: !isCompact && index > 0 ? 1 : 0,
            borderTopWidth: isCompact && index > 0 ? 1 : 0,
            borderColor: rule,
            gap: 13,
          }}
        >
          {renderItem(item, index)}
        </View>
      ))}
    </View>
  );
}

/**
 * The opening of a section: kicker, display heading, optional lede.
 *
 * Every section on these three pages starts this way, and repeating the three
 * elements plus their spacing inline was the single largest source of noise in
 * the section files.
 */
export function SectionIntro({
  kicker,
  lines,
  lede,
  type,
  tone,
  align,
  max,
  ledeMax = 560,
  children,
  style,
}) {
  const { band, width, display } = useLanding();
  const onBand = tone === 'band';
  const headingType = type || display({ min: 33, max: 57, leading: 1.0, tracking: -0.038 });

  return (
    <Reveal style={style}>
      {kicker ? <Kicker tone={tone} style={align === 'center' ? { justifyContent: 'center' } : undefined}>{kicker}</Kicker> : null}
      <DisplayHeading
        tag="h2"
        mask={false}
        type={headingType}
        align={align}
        color={onBand ? band.ink : undefined}
        style={{ marginTop: kicker ? 22 : 0, maxWidth: max }}
        lines={lines}
      />
      {lede ? (
        <Lede
          tone={tone}
          max={ledeMax}
          style={{
            marginTop: Math.round(width > 900 ? 22 : 18),
            ...(align === 'center' ? { textAlign: 'center', marginLeft: 'auto', marginRight: 'auto' } : {}),
          }}
        >
          {lede}
        </Lede>
      ) : null}
      {children}
    </Reveal>
  );
}
